// EquipmentTypeSelectionView.tsx
// Step 4 (special) for equipment-inspections:
// Premises → Equipment Inspections → Month → [Equipment Type] → Inspection List
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { usePremisesStore } from "../../store/store";
import { DotLoader } from "../../components/loader/Loader";
import { getPremiseName, getMonthName } from "./constants";
import axiosInstance from "../../common/axiosInstance";

interface EquipmentTypeSummary {
  id: string;
  name: string;
  assetCount: number;
  inspectionCount: number;
  passCount: number;
  failCount: number;
  attentionCount: number;
}

// Check if a string looks like a MongoDB ObjectId (24 hex chars) — kept for safety
const isObjectId = (s: string) => /^[a-f0-9]{24}$/i.test(s);

const EquipmentTypeSelectionView: React.FC = () => {
  const { premiseId, month } = useParams<{ premiseId: string; month: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { allPremises, fetchPremises } = usePremisesStore();

  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentTypeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const premiseName = getPremiseName(premiseId ?? "", allPremises);
  const monthName = getMonthName(Number(month));
  const year = searchParams.get("year") || String(new Date().getFullYear());

  useEffect(() => {
    if (!allPremises.length) fetchPremises();
  }, []);

  useEffect(() => {
    if (!premiseId || !month) return;
    setLoading(true);
    setError(null);
    axiosInstance
      .get("/api/inspection-assessments/monitoring/by-premises", {
        params: { premisesId: premiseId, month, year },
      })
      .then((r) => {
        const data = r.data?.data || r.data;
        setEquipmentTypes(data?.equipmentTypes || []);
      })
      .catch((e) => setError(e.response?.data?.message || "Failed to load equipment types"))
      .finally(() => setLoading(false));
  }, [premiseId, month, year]);

  const passRate = (t: EquipmentTypeSummary) =>
    t.inspectionCount > 0 ? Math.round((t.passCount / t.inspectionCount) * 100) : null;

  return (
    <div className="w-full px-6 mt-20 pb-10">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
          <li><Link to="/fire-system/monitoring" className="hover:text-[#00A8A5] font-medium transition-colors">Monitoring</Link></li>
          <li className="text-gray-300 select-none">›</li>
          <li><Link to={`/fire-system/monitoring/${premiseId}`} className="hover:text-[#00A8A5] transition-colors truncate max-w-[120px] inline-block">{premiseName}</Link></li>
          <li className="text-gray-300 select-none">›</li>
          <li><Link to={`/fire-system/monitoring/${premiseId}/equipment-inspections`} className="hover:text-[#00A8A5] transition-colors">Equipment Inspections</Link></li>
          <li className="text-gray-300 select-none">›</li>
          <li aria-current="page" className="text-gray-800 font-semibold">{monthName}</li>
        </ol>
      </nav>

      <button
        onClick={() => navigate(`/fire-system/monitoring/${premiseId}/equipment-inspections`)}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00A8A5] transition-colors mb-6 group"
      >
        <span className="group-hover:-translate-x-0.5 transition-transform">←</span>
        <span>Back</span>
      </button>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Select Equipment Type</h1>
        <p className="text-gray-500 text-sm mt-1">
          Viewing inspections for <span className="font-semibold text-gray-700">{premiseName}</span> — <span className="font-semibold text-gray-700">{monthName} {year}</span>
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24"><DotLoader /></div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      ) : equipmentTypes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="bg-gray-100 rounded-full p-5 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No inspections found</h3>
          <p className="text-gray-400 text-sm max-w-xs">No equipment inspections were recorded for {premiseName} in {monthName}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {equipmentTypes.map((et) => {
            const rate = passRate(et);
            const rateColor = rate === null ? "text-gray-400" : rate >= 80 ? "text-green-600" : rate >= 60 ? "text-yellow-600" : "text-red-600";
            const rateBg = rate === null ? "bg-gray-50" : rate >= 80 ? "bg-green-50" : rate >= 60 ? "bg-yellow-50" : "bg-red-50";
            // Resolve display name — use name directly, no ObjectId fallback needed
            const displayName = et.name && et.name !== et.id ? et.name : (et.name || "Unknown Equipment");
            return (
              <div
                key={et.id}
                onClick={() => navigate(`/fire-system/monitoring/${premiseId}/equipment-inspections/${month}/${encodeURIComponent(et.id)}?year=${year}`)}
                className="bg-white rounded-xl shadow-sm border-2 border-gray-100 hover:border-[#00A8A5] hover:shadow-lg hover:scale-[1.01] transition-all duration-200 cursor-pointer overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-[#00A8A5] to-[#007A78] px-5 py-4 flex items-center gap-3">
                  <div className="bg-white/20 rounded-lg p-2 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="text-white font-semibold text-sm truncate">{displayName}</span>
                </div>

                {/* Body */}
                <div className="p-5">
                  {/* Pass rate badge */}
                  {rate !== null && (
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold mb-4 ${rateBg} ${rateColor}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {rate}% Pass Rate
                    </div>
                  )}

                  {/* Stats grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-gray-800">{et.assetCount}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Assets</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-[#00A8A5]">{et.inspectionCount}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Inspections</div>
                    </div>
                  </div>

                  {/* Pass/Fail/Attention breakdown */}
                  {et.inspectionCount > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="flex items-center gap-1 text-green-600 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                        {et.passCount} Pass
                      </span>
                      <span className="text-gray-300">·</span>
                      <span className="flex items-center gap-1 text-yellow-600 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />
                        {et.attentionCount} Attention
                      </span>
                      <span className="text-gray-300">·</span>
                      <span className="flex items-center gap-1 text-red-600 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                        {et.failCount} Fail
                      </span>
                    </div>
                  )}

                  {/* Progress bar */}
                  {et.inspectionCount > 0 && (
                    <div className="mt-3 h-1.5 rounded-full overflow-hidden bg-gray-100 flex">
                      <div className="h-full bg-green-500" style={{ width: `${(et.passCount / et.inspectionCount) * 100}%` }} />
                      <div className="h-full bg-yellow-400" style={{ width: `${(et.attentionCount / et.inspectionCount) * 100}%` }} />
                      <div className="h-full bg-red-500" style={{ width: `${(et.failCount / et.inspectionCount) * 100}%` }} />
                    </div>
                  )}
                </div>

                {/* Footer CTA */}
                <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between">
                  <span className="text-xs text-gray-400">{et.inspectionCount} inspection{et.inspectionCount !== 1 ? "s" : ""} this month</span>
                  <span className="text-[#00A8A5] text-xs font-semibold flex items-center gap-1">
                    View Details
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EquipmentTypeSelectionView;
